import { doc, getDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, SBCard, SecondaryButton } from "@/components/ui";
import { db } from "@/firebase/app";
import {
  activeLeagueAdminMembership,
  activeMembership,
  canShowGPSAssignmentsForMembership,
  configuredHomeDashboardCards,
  HOME_DASHBOARD_CARD_IDS,
  HOME_DASHBOARD_CARD_LABELS,
  isMembershipCurrentlyActive,
  membershipId,
  parseUser,
  resolvedLeagueID,
  USER_ROLE_LABELS,
  type HomeDashboardCardID,
  type SBotMembership,
} from "@/models/user";
import type { LeagueMemberSummary } from "@/models/memberSummary";
import { orderByCompetitionRelevance } from "@/features/competitions/competitionDateOrdering";
import { updateStaffHomeCards } from "@/services/admin";
import { EventRow } from "./HomeDashboard";
import { useLeagueSnapshot } from "./useLeagueSnapshot";
import "./home.css";

// Drill-in list screens reached from the Home dashboard cards
// (CompetitionListView / OrganizationListView / LeagueAthletesListView on iOS).

export function ScheduleList() {
  const snapshot = useLeagueSnapshot(false);
  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>Schedule</h1>
      </div>
      <ErrorText>{snapshot.error}</ErrorText>
      {snapshot.loading && snapshot.competitions.length === 0 ? (
        <LoadingView />
      ) : snapshot.competitions.length === 0 ? (
        <SBCard>
          <p className="home-hint">No competitions yet.</p>
        </SBCard>
      ) : (
        <SBCard className="home-card">
          {snapshot.competitions.map((display) => (
            <EventRow key={display.instance.id} display={display} />
          ))}
        </SBCard>
      )}
    </div>
  );
}

export function OrganizationsList() {
  const snapshot = useLeagueSnapshot(false);
  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>Organizations</h1>
        <Link to="/organizations/new" className="badge badge-published">
          New organization
        </Link>
      </div>
      <ErrorText>{snapshot.error}</ErrorText>
      {snapshot.loading && snapshot.organizations.length === 0 ? (
        <LoadingView />
      ) : (
        <SBCard className="home-card">
          {snapshot.organizations.map((org) => (
            <Link key={org.id} to={`/organizations/${org.id}`} className="event-row">
              <div className="event-row-main">
                <strong>{org.name}</strong>
                <span>
                  {[org.abbreviation, org.location].filter(Boolean).join(" · ") || org.id}
                </span>
              </div>
              <span className="badge">{org.athleteUserIDs.length} athletes</span>
            </Link>
          ))}
          {snapshot.organizations.length === 0 && <p className="home-hint">No organizations yet.</p>}
        </SBCard>
      )}
    </div>
  );
}

export function PersonRow({ member }: { member: LeagueMemberSummary }) {
  const initials = member.displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
  return (
    <div className="person-row">
      <div className="person-avatar">{member.photoURL ? <img src={member.photoURL} alt="" /> : initials || "SB"}</div>
      <div className="person-main">
        <strong>{member.displayName}</strong>
        <span>{member.organizationNames.join(", ") || "League-wide"}</span>
      </div>
      <div className="person-roles">
        {member.roles.map((role) => (
          <span key={role} className="badge">
            {USER_ROLE_LABELS[role]}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * League-admin editor for a staff member's per-membership home cards — the
 * grant mechanism for opt-in cards like Broadcast Overlays and External Data
 * (POST /admin/staff/home-cards; mirrors isEditableHomeCardMembership).
 */
function StaffCardsEditor({ member, leagueID }: { member: LeagueMemberSummary; leagueID: string }) {
  const [memberships, setMemberships] = useState<SBotMembership[] | null>(null);
  const [selections, setSelections] = useState<Record<string, HomeDashboardCardID[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [savedID, setSavedID] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    void getDoc(doc(db, "users", member.userID))
      .then((snapshot) => {
        const profile = snapshot.exists() ? parseUser(member.userID, snapshot.data()) : null;
        const editable = (profile?.memberships ?? []).filter(
          (membership) =>
            isMembershipCurrentlyActive(membership) &&
            resolvedLeagueID(membership) === leagueID &&
            ((membership.targetType === "league" && (membership.role === "leagueManager" || membership.role === "leagueStaff")) ||
              (membership.targetType === "organization" &&
                (membership.role === "admin" || membership.role === "organizationManager" || membership.role === "organizationStaff"))),
        );
        setMemberships(editable);
        setSelections(
          Object.fromEntries(editable.map((membership) => [membershipId(membership), configuredHomeDashboardCards(membership)])),
        );
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Staff profile could not be loaded."));
  }, [member.userID, leagueID]);

  const save = async (membership: SBotMembership) => {
    const id = membershipId(membership);
    setIsBusy(true);
    setError(null);
    setSavedID(null);
    try {
      await updateStaffHomeCards({
        userID: member.userID,
        leagueID,
        membershipID: id,
        visibleHomeCardIDs: selections[id] ?? [],
      });
      setSavedID(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Cards could not be saved.");
    } finally {
      setIsBusy(false);
    }
  };

  if (!memberships) return <LoadingView label="Loading memberships…" />;
  if (memberships.length === 0) {
    return <p className="home-hint">No configurable staff memberships in this league (league admins always see every card).</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "6px 12px 12px" }}>
      {memberships.map((membership) => {
        const id = membershipId(membership);
        const selected = selections[id] ?? [];
        return (
          <div key={id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span className="home-hint">
              {USER_ROLE_LABELS[membership.role]} · {membership.displayName}
            </span>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {HOME_DASHBOARD_CARD_IDS.map((cardID) => (
                <label key={cardID} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={selected.includes(cardID)}
                    style={{ width: "auto" }}
                    onChange={(e) =>
                      setSelections((previous) => ({
                        ...previous,
                        [id]: e.target.checked ? [...selected, cardID] : selected.filter((value) => value !== cardID),
                      }))
                    }
                  />
                  {HOME_DASHBOARD_CARD_LABELS[cardID]}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <SecondaryButton title={isBusy ? "Saving…" : "Save cards"} onClick={() => void save(membership)} disabled={isBusy} />
              {savedID === id && <span className="home-hint">Saved.</span>}
            </div>
          </div>
        );
      })}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

export function RosterList({ filter }: { filter: "athlete" | "staff" }) {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const snapshot = useLeagueSnapshot(false);
  const [params] = useSearchParams();
  const organizationFilter = params.get("organization");
  const [expandedCardsFor, setExpandedCardsFor] = useState<string | null>(null);

  const canGrantCards =
    filter === "staff" &&
    snapshot.leagueID !== null &&
    (isSBotLabsAdmin || (currentUser ? activeLeagueAdminMembership(currentUser) !== undefined : false));

  let members = snapshot.memberSummaries.filter((member) =>
    filter === "athlete" ? member.roles.includes("athlete") : member.roles.some((role) => role !== "athlete"),
  );
  if (organizationFilter) {
    members = members.filter((member) => member.organizationIDs.includes(organizationFilter));
  }

  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>{filter === "athlete" ? "Athletes" : "Staff"}</h1>
      </div>
      <ErrorText>{snapshot.error}</ErrorText>
      {snapshot.loading && members.length === 0 ? (
        <LoadingView />
      ) : (
        <SBCard className="home-card">
          {members.map((member) => (
            <div key={member.id}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <PersonRow member={member} />
                </div>
                {canGrantCards && (
                  <SecondaryButton
                    title={expandedCardsFor === member.id ? "Close" : "Home cards"}
                    onClick={() => setExpandedCardsFor(expandedCardsFor === member.id ? null : member.id)}
                  />
                )}
              </div>
              {canGrantCards && expandedCardsFor === member.id && snapshot.leagueID && (
                <StaffCardsEditor member={member} leagueID={snapshot.leagueID} />
              )}
            </div>
          ))}
          {members.length === 0 && <p className="home-hint">Nobody here yet.</p>}
        </SBCard>
      )}
    </div>
  );
}

export function GpsMenu() {
  const { currentUser, isSBotLabsAdmin } = useAuth();
  const snapshot = useLeagueSnapshot(false);
  const gpsRaces = useMemo(
    () => orderByCompetitionRelevance(
      snapshot.competitions.filter((competition) => competition.isGPSRace),
      (competition) => competition,
    ),
    [snapshot.competitions],
  );
  const canManageGPS = isSBotLabsAdmin || (currentUser ? canShowGPSAssignmentsForMembership(activeMembership(currentUser)) : false);
  return (
    <div className="list-screen">
      <div className="list-screen-head">
        <h1>GPS</h1>
      </div>
      <ErrorText>{snapshot.error}</ErrorText>
      {snapshot.loading && gpsRaces.length === 0 ? (
        <LoadingView />
      ) : gpsRaces.length === 0 ? (
        <SBCard>
          <p className="home-hint">
            No GPS-enabled competitions. Mark a competition as a GPS Race (template field role "GPS Race") to enable the live
            map, assignments, and history.
          </p>
        </SBCard>
      ) : (
        gpsRaces.map((display) => (
          <SBCard key={display.instance.id} className="home-card">
            <div className="home-card-head">
              <h3>{display.eventName}</h3>
              <span className="badge badge-gps">GPS</span>
            </div>
            <div className="home-stats gps-menu-actions">
              <Link className="home-stat" to={`/competitions/${display.instance.id}/gps/map`}>
                <strong>Map</strong>
                <span>Live tracking</span>
              </Link>
              {canManageGPS && (
                <Link className="home-stat" to={`/competitions/${display.instance.id}/gps/assignments`}>
                  <strong>IDs</strong>
                  <span>Assignments</span>
                </Link>
              )}
              {canManageGPS && (
                <Link className="home-stat" to={`/competitions/${display.instance.id}/gps/share`}>
                  <strong>Share</strong>
                  <span>Public link</span>
                </Link>
              )}
              <Link className="home-stat" to={`/competitions/${display.instance.id}/gps/history`}>
                <strong>History</strong>
                <span>Playback</span>
              </Link>
              <Link className="home-stat" to={`/competitions/${display.instance.id}/gps/data`}>
                <strong>Data</strong>
                <span>Samples</span>
              </Link>
              {canManageGPS && (
                <Link className="home-stat" to={`/competitions/${display.instance.id}/overlays`}>
                  <strong>Overlays</strong>
                  <span>Broadcast</span>
                </Link>
              )}
            </div>
          </SBCard>
        ))
      )}
    </div>
  );
}
